import BookImage from '@components/BookImage'
import { TranslationKey } from '@hermes/translations'
import { BookDetailsPageResponse } from '@hermes/types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  BookCard,
  Box,
  Button,
  Chip,
  SlicedLine,
  Typography,
  useMediaQuery,
  useTheme
} from '@hermes/web-components'
import { useLocale } from '@hooks/useLocale'
import { useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { getIndexFromLocale } from '@lib/utils/searchEngineHelpers'
import { useRouter } from 'next/navigation'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { AccordionLine, ContentContainer, GenresContainer, ShowMoreButton } from './DetailsSection.styles'
import Reviews from './Reviews'

interface ContentProps {
  details: BookDetailsPageResponse['data']
  aboutRef: React.RefObject<HTMLDivElement>
  reviewsRef: React.RefObject<HTMLDivElement>
  versionsRef: React.RefObject<HTMLDivElement>
  setVersionsModalShow: Dispatch<SetStateAction<boolean>>
}

const DetailsSection = ({ details, aboutRef, reviewsRef, versionsRef, setVersionsModalShow }: ContentProps) => {
  const router = useRouter()
  const locale = useLocale()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslateMessage()
  const navigation = useNavigation()
  const [showMoreGenres, setShowMoreGenres] = useState(false)
  const sortedGenres = details.genres?.sort((a, b) => a.id - b.id)

  const genresToDisplay = showMoreGenres ? sortedGenres : sortedGenres?.slice(0, 3)
  const bookInfo = [
    { key: t({ id: 'bdp.info.isbn' }), value: details.fullIsbn },
    { key: t({ id: 'bdp.info.publisher' }), value: details.publisher },
    { key: t({ id: 'bdp.info.year' }), value: details.year },
    { key: t({ id: 'language' }), value: details.languages.map((lang) => t({ id: lang.code as TranslationKey })) },
    { key: t({ id: 'bdp.info.edition' }), value: details.editionNumber },
    { key: t({ id: 'bdp.info.originalTitle' }), value: details.title },
    { key: t({ id: 'format' }), value: t({ id: details.format }) }
  ]
  const shippingAndReturns = [
    {
      key: t({ id: 'shipping' }),
      value: t({ id: 'bdp.shippingAndReturns.shipping' }, { days: '2-4' })
    },
    { key: t({ id: 'returns' }), value: t({ id: 'bdp.shippingAndReturns.returns' }) }
  ]
  const genreRegion =
    {
      no: 'titleNo',
      se: 'titleSv',
      'en-no': 'titleEn',
      'en-se': 'titleEn'
    }[locale] || 'titleEn'

  const BookInfoAccordion = ({
    title,
    items
  }: {
    title: string
    items: { key: string; value: string | number | string[] | null }[]
  }) => (
    <Accordion>
      <AccordionSummary
        sx={{
          marginTop: '-1px'
        }}
      >
        <Typography fontSize="14px" fontWeight={500}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '0px'
        }}
      >
        {items.map(
          (info, index) =>
            info.value && (
              <AccordionLine key={index}>
                <Typography fontWeight={500} fontSize="14px" color={theme.palette.neutral[600]}>
                  {info.key}
                </Typography>
                <Typography fontSize="14px" maxWidth="50%" color={theme.palette.neutral[900]}>
                  {info.value}
                </Typography>
              </AccordionLine>
            )
        )}
      </AccordionDetails>
    </Accordion>
  )

  const handleChipClick = (genre: ContentProps['details']['genres'][number]) => {
    const index = getIndexFromLocale(locale)
    navigation.navigate.searchPage('', genre?.code || '', index)
  }

  return (
    <ContentContainer ref={aboutRef}>
      <Typography variant="h2" fontSize="24px" marginBottom="24px">
        {t({ id: 'bdp.aboutBook' })}
      </Typography>
      <Typography whiteSpace="break-spaces" fontSize="14px" lineHeight="24px" sx={{ wordBreak: 'break-word' }}>
        <SlicedLine
          text={details.description ?? ''}
          max={500}
          showMoreText={t({ id: 'showMore' }) as string}
          hideMoreText={t({ id: 'bdp.descriptionHide' }) as string}
          readMore
          triggerSx={{
            fontSize: '14px',
            color: 'primary.main'
          }}
        />
      </Typography>

      {Boolean(genresToDisplay?.length) && (
        <GenresContainer>
          {genresToDisplay?.map((genre, index) => (
            <Chip
              onClick={() => handleChipClick(genre)}
              variant="filled"
              color="info"
              label={genre[genreRegion as keyof typeof genre]}
              key={index}
            />
          ))}
          {Boolean(details.genres && details.genres.length > 3) && (
            <ShowMoreButton variant="ghost" onClick={() => setShowMoreGenres(!showMoreGenres)}>
              {showMoreGenres ? t({ id: 'hideMore' }) : t({ id: 'showMore' })}
            </ShowMoreButton>
          )}
        </GenresContainer>
      )}
      <Box mt={isMobile ? '40px' : '20px'}>
        {BookInfoAccordion({ title: String(t({ id: 'bdp.bookDetails' })), items: bookInfo })}
        {BookInfoAccordion({ title: String(t({ id: 'bdp.shippingAndReturns' })), items: shippingAndReturns })}
      </Box>
      {Boolean(details.versions?.length) && (
        <Box ref={versionsRef} mt="48px">
          <Box mb="12px" display="flex" justifyContent="space-between">
            <Typography variant="h2" fontSize="24px">
              {t({ id: 'bookDetails.OtherVersions' })}
            </Typography>
            {Boolean(details.versions && details.versions.length > 2) && (
              <Button
                onClick={() => setVersionsModalShow(true)}
                sx={{
                  padding: '7px 12px',
                  height: 'auto',
                  fontSize: '12px',
                  lineHeight: '16px'
                }}
                variant="outlined"
              >
                {t({ id: 'bookDetails.SeeAll' })}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              '.list-version-item': {
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-child': {
                  borderBottom: 'none'
                }
              }
            }}
          >
            {(details.versions && details.versions.length > 2 ? details.versions.slice(0, 2) : details.versions)?.map(
              (version) => (
                <Box className="list-version-item" key={version.id}>
                  <BookCard
                    inline
                    title={`${version.title}`}
                    cover={`${t({ id: `${version.format}` })}`}
                    chipLabel={''}
                    globalAction={() => router.push(`/${locale}/books/${version.slug}`)}
                    image={<BookImage src={version.smallImage} width={40} height={56} alt={version.title ?? ''} />}
                    year={String(version.year)}
                    hover
                  />
                </Box>
              )
            )}
          </Box>
        </Box>
      )}
      <Reviews
        key={details.id}
        bookId={details.id}
        reviewsRef={reviewsRef}
        ratingData={{ votes: details.rating?.votes ?? 0, avgRating: details.rating?.avgRating ?? 0 }}
      />
    </ContentContainer>
  )
}

export default DetailsSection
